import React, { useCallback } from 'react';

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

// 错误状态接口
export interface ErrorState {
  status: number | string;
  message: string;
  timestamp?: number;
}

// 默认错误状态
const defaultErrorState: ErrorState = {
  status: 200,
  message: '',
  timestamp: Date.now(),
};

// Store 接口
interface ErrStatusStore {
  errorMap: Record<string, ErrorState>;
  setError: (key: string, errInfo: ErrorState) => void;
  clearError: (key: string) => void;
  reset: () => void;
}

// 创建 store
export const useErrStatusStore = create<ErrStatusStore>()(
  subscribeWithSelector((set, get) => ({
    errorMap: {},
    setError: (key: string, errInfo: ErrorState) => {
      set((state) => ({
        ...state,
        errorMap: {
          ...state.errorMap,
          [key]: {
            ...errInfo,
            timestamp: Date.now(),
          },
        },
      }));
    },
    clearError: (key: string) => {
      set((state) => ({
        ...state,
        errorMap: { ...state.errorMap, [key]: { ...defaultErrorState } },
      }));
    },
    reset: () => {
      set({
        errorMap: {},
      });
    },
  })),
);

// 全局错误处理器 - 用于在非 React 组件中触发错误
export const globalErrorHandler = {
  // 设置错误
  setError: (key: string, errInfo: ErrorState) => {
    useErrStatusStore.getState().setError(key, errInfo);
  },

  // 清除错误
  clearError: (key: string) => {
    useErrStatusStore.getState().clearError(key);
  },

  // 重置所有状态
  reset: () => {
    useErrStatusStore.getState().reset();
  },
};

// 便捷的 Hook 用于获取指定模块错误
export const useError = (key: string = window.location.pathname) => {
  key = key || window.location.pathname;
  const error = useErrStatusStore(React.useCallback((state) => state.errorMap[key], [key]));
  const _clearError = useErrStatusStore((state) => state.clearError);
  const _setError = useErrStatusStore((state) => state.setError);

  const clearError = useCallback(() => {
    _clearError(key);
  }, [_clearError, key]);

  const setError = useCallback(
    (errInfo: ErrorState, k?: string) => {
      _setError(k || key, errInfo);
    },
    [_setError, key],
  );

  return {
    error,
    hasError: !!error && error.status !== 200,
    clearError,
    setError,
  };
};

// 路由变化监听器
export const setupRouteChangeListener = () => {
  let isProcessing = false;
  let lastPath = window.location.pathname;

  const handleRouteChange = () => {
    // 防止重复处理
    if (isProcessing) return;

    const currentPath = window.location.pathname;
    // 只有当路径真正变化时才清除错误
    if (currentPath !== lastPath) {
      isProcessing = true;
      lastPath = currentPath;

      // 使用 setTimeout 确保在下一个事件循环中执行，避免在事件处理过程中修改状态
      setTimeout(() => {
        const clearError = useErrStatusStore.getState().clearError;
        clearError(currentPath);
        isProcessing = false;
      }, 0);
    }
  };

  // 监听 popstate 事件（浏览器前进后退）
  window.addEventListener('popstate', handleRouteChange);

  // 监听 pushstate 和 replacestate 事件
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(history, args);
    // 延迟处理，确保路由变化完成
    setTimeout(handleRouteChange, 0);
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(history, args);
    // 延迟处理，确保路由变化完成
    setTimeout(handleRouteChange, 0);
  };

  // 返回清理函数
  return () => {
    window.removeEventListener('popstate', handleRouteChange);
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
  };
};

// 全局错误事件监听器
export const setupGlobalErrorListener = () => {
  const setError = useErrStatusStore.getState().setError;

  const handleGlobalError = (event: CustomEvent) => {
    const { errInfo } = event.detail;
    setError('global', errInfo);
  };

  window.addEventListener('errStatus', handleGlobalError as EventListener);

  // 返回清理函数
  return () => {
    window.removeEventListener('errStatus', handleGlobalError as EventListener);
  };
};
